import type { ReactNode } from 'react';

export interface CommandItem {
  id: string;
  group: string;
  label: string;
  /** Shown before the label, e.g. an app's icon. */
  icon?: ReactNode;
  /** Secondary text shown on the right, e.g. the app a page belongs to. */
  hint?: string;
  /** A second line under the label, e.g. a docs excerpt. */
  description?: string;
  /** Extra words that should match, e.g. "api" for Access tokens. */
  keywords?: string;
  external?: boolean;
  /** Only listed once the user types, e.g. individual builds. */
  searchOnly?: boolean;
  run: () => void;
}

const normalise = (value: string) => value.toLowerCase().trim();

const score = (item: CommandItem, query: string): number => {
  const label = normalise(item.label);
  if (label === query) return 0;
  if (label.startsWith(query)) return 1;
  if (label.split(/\s+/).some((word) => word.startsWith(query))) return 2;
  if (label.includes(query)) return 3;
  const rest = normalise(
    [item.group, item.hint, item.keywords].filter(Boolean).join(' '),
  );
  if (rest.includes(query)) return 4;
  return -1;
};

/**
 * Filters commands by every word of the query and orders them by how well the
 * label matches. Without a query, items keep their original grouping.
 */
export const filterCommands = (
  items: CommandItem[],
  rawQuery: string,
): CommandItem[] => {
  const words = normalise(rawQuery).split(/\s+/).filter(Boolean);
  if (words.length === 0) return items.filter((item) => !item.searchOnly);

  return items
    .map((item, index) => {
      const scores = words.map((word) => score(item, word));
      if (scores.some((s) => s < 0)) return null;
      return { item, index, rank: Math.max(...scores) };
    })
    .filter((entry): entry is NonNullable<typeof entry> => entry !== null)
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map(({ item }) => item);
};

/** Groups items while keeping the order in which groups first appear. */
export const groupCommands = (
  items: CommandItem[],
): { group: string; items: CommandItem[] }[] => {
  const groups = new Map<string, CommandItem[]>();
  for (const item of items) {
    const list = groups.get(item.group) ?? [];
    list.push(item);
    groups.set(item.group, list);
  }
  return Array.from(groups, ([group, groupItems]) => ({
    group,
    items: groupItems,
  }));
};

/**
 * Whether a keydown is ⌘K / Ctrl+K. `key` is typed as a string but can be
 * missing: Chrome fires a plain `keydown` Event when an autofill suggestion is
 * picked, and some extensions dispatch synthetic ones.
 */
export const isCommandPaletteShortcut = (
  event: Pick<
    KeyboardEvent,
    'key' | 'metaKey' | 'ctrlKey' | 'altKey' | 'shiftKey'
  >,
): boolean =>
  typeof event.key === 'string' &&
  event.key.toLowerCase() === 'k' &&
  (event.metaKey || event.ctrlKey) &&
  !event.altKey &&
  !event.shiftKey;
